import { useGetSubscription } from "@/api/stripe";
import { useOrgUsers, usePendingInvites } from "@/api/users";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useInvoicePreview } from "@/hooks/useInvoicePreview";
import { formatCurrency } from "@/utils/currency";
import type { PlanSelection, TransformedPlan } from "@/utils/planChange";
import { selectionEndsTrial, TRIAL_MAX_SEATS } from "@/utils/planChange";
import { BillingInterval } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface PlanConfigurationModalProps {
  plan: TransformedPlan;
  open: boolean;
  buttonLoading?: boolean;
  redirectTo?: string;
  upgradeMessage?: string;
  /** When true, shows trial-specific messaging ($0 due today, etc.) */
  isTrialSignup?: boolean;
  /** Number of trial days (default 14) */
  trialDays?: number;
  onComplete: (selection: PlanSelection) => void;
  onClose: () => void;
}

export function PlanConfigurationModal({
  plan,
  open,
  buttonLoading = false,
  redirectTo,
  isTrialSignup = false,
  trialDays = 14,
  onComplete,
  onClose,
}: PlanConfigurationModalProps) {
  const { data: subscription } = useGetSubscription();

  // Determine initial billing cycle based on plan's available intervals
  const getInitialBillingCycle = (): BillingInterval => {
    if (plan.available_intervals.includes("year")) {
      return "year";
    } else if (plan.available_intervals.includes("month")) {
      return "month";
    }
    return "year"; // fallback
  };

  const [billingCycle, setBillingCycle] = useState<BillingInterval>(
    getInitialBillingCycle(),
  );
  const { data: orgUsers = [], isLoading: isOrgUsersLoading } = useOrgUsers();
  const { data: pendingInvites = [], isLoading: isPendingInvitesLoading } =
    usePendingInvites();

  const isUserCountLoading = isOrgUsersLoading || isPendingInvitesLoading;

  // Calculate current total users (active + pending) - this becomes the minimum
  const currentUserCount = orgUsers.length + pendingInvites.length;
  const minimumUsers = Math.max(1, currentUserCount);
  const [userCount, setUserCount] = useState(
    subscription?.data.stripe_subscription?.seat_count || 0,
  );

  // Update userCount when minimum changes
  useEffect(() => {
    if (userCount < minimumUsers) {
      setUserCount(minimumUsers);
    }
  }, [minimumUsers, userCount]);

  // Reset billing cycle when plan changes to ensure it's valid
  useEffect(() => {
    const validCycle = getInitialBillingCycle();
    setBillingCycle(validCycle);
  }, [plan]);

  // Check if user has a yearly subscription - if so, they can't switch to monthly
  const isYearlySubscription =
    subscription?.data.stripe_subscription?.billing_interval === "year";

  // Show both options only if:
  // 1. User doesn't have a yearly subscription constraint, AND
  // 2. The plan has both billing intervals available
  const showBothOptions =
    !isYearlySubscription &&
    plan.available_intervals.includes("year") &&
    plan.available_intervals.includes("month");

  // Use the invoice preview hook (now handles all fallback logic internally)
  const {
    totalPrice,
    pricePerSeat: currentPricePerSeat,
    discount,
    isLoading: showLoading,
    lineItems,
  } = useInvoicePreview(
    {
      planName: plan.name,
      billingInterval: billingCycle,
      seatCount: userCount,
      enabled: open, // Only preview when modal is open
    },
    {
      debounceMs: 500,
      autoPreview: true,
    },
  );

  const handleUserCountChange = (change: number) => {
    const newCount = Math.max(minimumUsers, userCount + change);
    setUserCount(newCount);
  };

  const handleContinue = () => {
    onComplete({
      plan,
      billing_cycle: billingCycle,
      user_count: userCount,
      redirectTo,
    });
  };

  const endsTrial = selectionEndsTrial(
    { plan, billing_cycle: billingCycle, user_count: userCount },
    subscription?.data.stripe_subscription,
  );

  // Helper function to format line item amounts
  const formatLineItemAmount = (amount: number) => {
    const dollars = Math.abs(amount) / 100;
    const isNegative = amount < 0;
    return {
      dollars,
      isNegative,
      formatted: `${isNegative ? "-" : "+"}${formatCurrency(dollars)}`,
    };
  };

  // Calculate the regular monthly total (what they'll pay each month)
  const monthlyTotal = currentPricePerSeat * userCount;

  // Only show billing start text if today's total differs from monthly total
  const showBillingStartText = !showLoading && totalPrice === monthlyTotal;

  const isTrial = subscription?.data.stripe_subscription?.status === "trialing";
  const hasPaymentMethod =
    subscription?.data.stripe_subscription?.default_payment_method_id;

  const buttonText = useMemo(() => {
    if (
      isTrial &&
      endsTrial &&
      subscription?.data.stripe_subscription?.default_payment_method_id
    ) {
      return "Update Plan & End Trial";
    }

    if (hasPaymentMethod) {
      return "Update Plan";
    }

    return "Continue to Billing";
  }, [subscription, isTrial, endsTrial]);

  if (isUserCountLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={showBothOptions ? "!max-w-4xl" : "!max-w-2xl"}>
        <DialogHeader>
          <DialogTitle>
            {isTrialSignup
              ? `Get Cohesive free for ${trialDays} days`
              : `Your ${plan.display_name} Plan`}
          </DialogTitle>
          <DialogDescription>
            {isTrialSignup
              ? "$0 due today. Cancel any time."
              : "Configure your plan to fit your needs."}
          </DialogDescription>
        </DialogHeader>

        {endsTrial && (
          <Alert
            data-testid="trial-end-warning"
            className="py-2 px-3 border-rust/30 bg-rust/10 text-rust dark:border-rust/40 dark:bg-rust/10 dark:text-rust"
          >
            <AlertDescription>
              Free trials cover up to {TRIAL_MAX_SEATS} seats. Continuing with{" "}
              {userCount} seats ends your trial and starts billing immediately.
            </AlertDescription>
          </Alert>
        )}

        {showBothOptions ? (
          // Two column layout when billing options are available
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - Configuration */}
            <div className="space-y-6">
              {/* Users Section */}
              <div>
                <h3 className="text-md font-medium text-foreground mb-4">
                  Users
                </h3>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      data-testid="user-count-input"
                      type="number"
                      min={minimumUsers.toString()}
                      value={userCount}
                      onChange={(e) =>
                        setUserCount(
                          Math.max(
                            minimumUsers,
                            parseInt(e.target.value) || minimumUsers,
                          ),
                        )
                      }
                    />
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            data-testid="user-count-minus"
                            variant="outline"
                            size="icon"
                            onClick={() => handleUserCountChange(-1)}
                            disabled={userCount <= minimumUsers}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {userCount === minimumUsers && userCount !== 1 && (
                        <TooltipContent>
                          <p>
                            You must remove users from your account before you
                            can reduce the number of seats
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  <Button
                    data-testid="user-count-plus"
                    variant="outline"
                    size="icon"
                    onClick={() => handleUserCountChange(1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Billing Cycle Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">
                  Billing Cycle
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Annual Plan - Only show if available */}
                  {plan.available_intervals.includes("year") && (
                    <div
                      data-testid="annual-plan-option"
                      className={`border-2 rounded-lg p-4 pt-5 cursor-pointer transition-colors relative ${
                        billingCycle === "year"
                          ? "border-primary bg-accent"
                          : "border-input bg-background hover:bg-accent"
                      }`}
                      onClick={() => setBillingCycle("year")}
                    >
                      {/* Save Badge */}
                      <div className="absolute -top-2 -left-2 bg-spectrum-green dark:bg-lc-active text-white text-xs font-medium px-2 py-1 rounded-md">
                        Save 16%
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-foreground">
                          Annual
                        </span>
                        <div
                          className={`w-4 h-4 rounded-full ${
                            billingCycle === "year"
                              ? "bg-foreground"
                              : "border-2 border-input bg-background"
                          }`}
                        >
                          {billingCycle === "year" && (
                            <div className="w-2 h-2 bg-background rounded-full m-1"></div>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground mb-2">
                        {plan.monthly_price && plan.yearly_price ? (
                          <>
                            <span className="line-through">
                              {formatCurrency(plan.monthly_price)}
                            </span>{" "}
                            {formatCurrency(plan.yearly_price / 12)}
                          </>
                        ) : (
                          <span>
                            {plan.yearly_price
                              ? formatCurrency(plan.yearly_price / 12)
                              : "N/A"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        per user/month
                      </div>
                      <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                        <li>• Billed annually</li>
                        <li>• Add and reassign users as needed</li>
                      </ul>
                    </div>
                  )}

                  {/* Monthly Plan - Only show if available */}
                  {plan.available_intervals.includes("month") && (
                    <div
                      data-testid="monthly-plan-option"
                      className={`border-2 rounded-lg p-4 pt-5 cursor-pointer transition-colors ${
                        billingCycle === "month"
                          ? "border-foreground bg-accent"
                          : "border-input bg-background hover:bg-accent"
                      }`}
                      onClick={() => setBillingCycle("month")}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-foreground">
                          Monthly
                        </span>
                        <div
                          className={`w-4 h-4 rounded-full ${
                            billingCycle === "month"
                              ? "bg-foreground"
                              : "border-2 border-input bg-background"
                          }`}
                        >
                          {billingCycle === "month" && (
                            <div className="w-2 h-2 bg-background rounded-full m-1"></div>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground mb-2">
                        {plan.monthly_price
                          ? formatCurrency(plan.monthly_price)
                          : "N/A"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        per user/month
                      </div>
                      <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                        <li>• Billed monthly</li>
                        <li>• Add or remove users as needed</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Summary */}
            <Card data-testid="summary-section" className="bg-muted/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-medium text-foreground mb-4">
                  Summary
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-foreground">
                        {plan.display_name} Plan
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {userCount} users •{" "}
                        {formatCurrency(currentPricePerSeat)}/seat
                      </div>
                    </div>
                    <div className="text-right">
                      {showLoading ? (
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-16 ml-auto" />
                          <Skeleton className="h-4 w-20 ml-auto" />
                        </div>
                      ) : (
                        <>
                          <div className="font-medium text-foreground">
                            {formatCurrency(monthlyTotal)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            per month
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Line Items Breakdown */}
                  {(lineItems.length > 0 || showLoading) && (
                    <div className="space-y-2 pt-3 border-t">
                      <div className="text-sm font-medium text-foreground mb-2">
                        Billing Details
                      </div>
                      {showLoading ? (
                        // Skeleton loading state
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 pr-2">
                              <Skeleton className="h-4 w-3/4 mb-1" />
                              <Skeleton className="h-3 w-12" />
                            </div>
                            <Skeleton className="h-4 w-16" />
                          </div>
                          <div className="flex justify-between items-start">
                            <div className="flex-1 pr-2">
                              <Skeleton className="h-4 w-2/3 mb-1" />
                              <Skeleton className="h-3 w-12" />
                            </div>
                            <Skeleton className="h-4 w-16" />
                          </div>
                        </div>
                      ) : (
                        lineItems.map((item, index) => {
                          const { isNegative, formatted } =
                            formatLineItemAmount(item.amount);
                          return (
                            <div
                              key={index}
                              className="flex justify-between items-start"
                            >
                              <div className="flex-1 pr-2">
                                <div className="text-sm text-foreground">
                                  {item.description}
                                </div>
                                {item.quantity > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    Qty: {item.quantity}
                                  </div>
                                )}
                              </div>
                              <div
                                className={`text-sm font-medium ${
                                  isNegative
                                    ? "text-lc-active dark:text-lc-active"
                                    : "text-foreground"
                                }`}
                              >
                                {formatted}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {discount > 0 && (
                    <div className="flex justify-between">
                      <div className="text-sm text-muted-foreground">
                        Discount
                      </div>
                      {showLoading ? (
                        <Skeleton className="h-4 w-12" />
                      ) : (
                        <div className="text-sm text-lc-active dark:text-lc-active">
                          -{formatCurrency(discount)}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-foreground">
                      {isTrialSignup ? "Due today" : "Today's total"}
                    </div>
                    {showLoading ? (
                      <Skeleton className="h-6 w-24 mb-1" />
                    ) : (
                      <div className="font-medium text-lg text-foreground">
                        {isTrialSignup ? "$0.00" : formatCurrency(totalPrice)}
                      </div>
                    )}
                  </div>
                  {!isTrialSignup && showBillingStartText && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Billed {billingCycle === "year" ? "annually" : "monthly"}{" "}
                      starting today
                    </div>
                  )}
                </div>

                <Button
                  data-testid="continue-button"
                  onClick={handleContinue}
                  className="w-full py-3 font-medium"
                  isLoading={buttonLoading}
                  disabled={showLoading}
                >
                  {buttonText}
                </Button>

                {isTrialSignup && (
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    After the trial period, you will be charged{" "}
                    {formatCurrency(monthlyTotal)} per{" "}
                    {billingCycle === "year" ? "year" : "month"} unless you
                    cancel before the trial expires.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          // Single column layout when no billing options
          <div className="space-y-6">
            {/* Users Section */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">
                Users
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Input
                    data-testid="user-count-input"
                    type="number"
                    min={minimumUsers.toString()}
                    value={userCount}
                    onChange={(e) =>
                      setUserCount(
                        Math.max(
                          minimumUsers,
                          parseInt(e.target.value) || minimumUsers,
                        ),
                      )
                    }
                  />
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          data-testid="user-count-minus"
                          variant="outline"
                          size="icon"
                          onClick={() => handleUserCountChange(-1)}
                          disabled={userCount <= minimumUsers}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {userCount === minimumUsers && userCount !== 1 && (
                      <TooltipContent>
                        <p>
                          You must remove users from your account before you can
                          reduce the number of seats
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <Button
                  data-testid="user-count-plus"
                  variant="outline"
                  size="icon"
                  onClick={() => handleUserCountChange(1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Summary Card */}
            <Card data-testid="summary-section" className="bg-muted/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-medium text-foreground mb-4">
                  Summary
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-foreground">
                        {plan.display_name} Plan
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {userCount} users •{" "}
                        {formatCurrency(currentPricePerSeat)}/seat
                      </div>
                    </div>
                    <div className="text-right">
                      {showLoading ? (
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-16 ml-auto" />
                          <Skeleton className="h-4 w-20 ml-auto" />
                        </div>
                      ) : (
                        <>
                          <div className="font-medium text-foreground">
                            {formatCurrency(monthlyTotal)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            per month
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Line Items Breakdown */}
                  {(lineItems.length > 0 || showLoading) && (
                    <div className="space-y-2 pt-3 border-t">
                      <div className="text-sm font-medium text-foreground mb-2">
                        Billing Details
                      </div>
                      {showLoading ? (
                        // Skeleton loading state
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 pr-2">
                              <Skeleton className="h-4 w-3/4 mb-1" />
                              <Skeleton className="h-3 w-12" />
                            </div>
                            <Skeleton className="h-4 w-16" />
                          </div>
                          <div className="flex justify-between items-start">
                            <div className="flex-1 pr-2">
                              <Skeleton className="h-4 w-2/3 mb-1" />
                              <Skeleton className="h-3 w-12" />
                            </div>
                            <Skeleton className="h-4 w-16" />
                          </div>
                        </div>
                      ) : (
                        lineItems.map((item, index) => {
                          const { isNegative, formatted } =
                            formatLineItemAmount(item.amount);
                          return (
                            <div
                              key={index}
                              className="flex justify-between items-start"
                            >
                              <div className="flex-1 pr-2">
                                <div className="text-sm text-foreground">
                                  {item.description}
                                </div>
                                {item.quantity > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    Qty: {item.quantity}
                                  </div>
                                )}
                              </div>
                              <div
                                className={`text-sm font-medium ${
                                  isNegative
                                    ? "text-lc-active dark:text-lc-active"
                                    : "text-foreground"
                                }`}
                              >
                                {formatted}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {discount > 0 && (
                    <div className="flex justify-between">
                      <div className="text-sm text-muted-foreground">
                        Discount
                      </div>
                      {showLoading ? (
                        <Skeleton className="h-4 w-12" />
                      ) : (
                        <div className="text-sm text-lc-active dark:text-lc-active">
                          -{formatCurrency(discount)}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-foreground">
                      {isTrialSignup ? "Due today" : "Today's total"}
                    </div>
                    {showLoading ? (
                      <Skeleton className="h-6 w-24 mb-1" />
                    ) : (
                      <div className="font-medium text-lg text-foreground">
                        {isTrialSignup ? "$0.00" : formatCurrency(totalPrice)}
                      </div>
                    )}
                  </div>
                  {!isTrialSignup && showBillingStartText && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Billed {billingCycle === "year" ? "annually" : "monthly"}{" "}
                      starting today
                    </div>
                  )}
                </div>

                <Button
                  data-testid="continue-button"
                  onClick={handleContinue}
                  className="w-full py-3 font-medium"
                  isLoading={buttonLoading}
                  disabled={showLoading}
                >
                  {buttonText}
                </Button>

                {isTrialSignup && (
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    After the trial period, you will be charged{" "}
                    {formatCurrency(monthlyTotal)} per{" "}
                    {billingCycle === "year" ? "year" : "month"} unless you
                    cancel before the trial expires.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
